import { useState } from "react";
import { BRAND } from "../constants/brand";
import PageHero from "../components/PageHero";

const LEADERS = [
  {
    name: "Brian & Kerry Meshkin",
    title: "Co-Founders",
    initials: "BK",
    color: BRAND.navy,
    paragraphs: [
      "Brian Meshkin is the co-founder and Chief Executive Officer of Labarium Diagnostic Systems, where he is building a category-defining diagnostics platform designed to scale precision medicine through advanced analytics, integrated laboratory infrastructure, and payer-aligned commercialization. A repeat healthcare entrepreneur, Mr. Meshkin has founded and led multiple high-growth diagnostics companies, including Proove Biosciences, which was an early pioneer in clinical-genetic analytics and real-world evidence generation.",
      "Under his leadership, organizations have achieved rapid scale and national market penetration, earning recognition on the Inc. 500 and Deloitte Fast 500, along with honors as a two-time Ernst & Young Entrepreneur of the Year finalist and OCTANe CEO of the Year. Earlier in his career, he held commercial innovation roles at Eli Lilly and Company's e.Lilly Venture Fund and Johnson & Johnson.",
      "Mr. Meshkin's life journey as a social entrepreneur began at 13 years of age following the tragic death of a friend, where he championed a student-led effort to enact the first bicycle helmet law for children in the United States, and helped start Safe Kids Worldwide to expand his efforts in Maryland across the nation. Having been a successful community activist, non-profit leader, elected official, innovation executive, and award-winning entrepreneur, Mr. Meshkin has experienced multiple perspectives in his quest to solve problems to save, extend, and improve the quality of life. He is also an adjunct professor and frequent lecturer on healthcare innovation, with a focus on translating emerging science into scalable, reimbursement-driven clinical solutions.",
      "Kerry Meshkin, his wife, is a co-founder and has a successful professional background in healthcare marketing, clinical practice management, and political consulting.",
    ],
    expertise: ["Strategic Vision", "Healthcare Entrepreneurship", "Precision Medicine"],
  },
  {
    name: "James Lilly",
    title: "Vice President, Finance",
    initials: "JL",
    color: "#0d3d6e",
    paragraphs: [
      "James Lilly is Vice President of Finance at Labarium Diagnostic Systems, where he leads financial strategy, capital formation, and transaction execution in support of the company's platform-driven growth and category-defining ambitions in precision diagnostics. He is responsible for aligning Labarium's capital strategy with its expansion roadmap, including scaling operations, advancing payer-aligned commercialization, and executing strategic M&A initiatives.",
      "Mr. Lilly brings more than 30 years of experience in healthcare investment banking and private capital markets, with deep expertise across diagnostics, healthcare services, and health technology. Over the course of his career, he has advised companies across all stages of growth on mergers and acquisitions, equity financings, and debt capital raises, supporting transactions from early-stage capital formation through large-scale strategic exits. His experience spans working with founder-led growth companies as well as institutional investors, positioning businesses for disciplined scale and long-term value creation. He has held previous senior roles leading healthcare investment banking at Jeffries, Cowen and Company, Piper, England & Company, and SI Capital.",
      "At Labarium, Mr. Lilly plays a central role in shaping the company's financial architecture and capital markets strategy, partnering closely with executive leadership to drive scalable growth, optimize capital deployment, and position the platform for potential strategic or public market outcomes.",
    ],
    expertise: ["Capital Formation", "Healthcare Investment Banking", "M&A Execution"],
  },
  {
    name: "Mohit Khan",
    title: "Vice President, Operations",
    initials: "MK",
    color: "#0f4878",
    paragraphs: [
      "Mohit Khan is Vice President of Operations at Labarium Diagnostic Systems, where he leads the design and execution of a scalable, high-performance laboratory and operational platform. He is responsible for driving throughput, quality, cost efficiency, and infrastructure readiness to support national growth.",
      "Mr. Khan brings extensive experience in managing complex, high-volume clinical operations, with a focus on process engineering, automation, and continuous improvement. He has built and optimized operational systems that materially improve turnaround times, reduce cost per test, and maintain rigorous compliance with regulatory and quality standards. Previously, Mr. Khan has served in senior operational roles including the Administrative Director of the Hospitals Labs in the University of Maryland Medical System, American International Medical Services, Alere Toxicology, Quest Diagnostics, True Health Diagnostics, and LabCorp.",
      "At Labarium, he is a key architect of the company's scalable infrastructure, enabling rapid expansion while preserving clinical excellence and operational discipline.",
    ],
    expertise: ["Lab Operations", "Process Engineering", "Scalable Infrastructure"],
  },
  {
    name: "Rosland McLeod, Esq.",
    title: "Vice President, Legal & Compliance",
    initials: "RM",
    color: "#163d6a",
    paragraphs: [
      "Rosland McLeod is Vice President of Legal & Compliance at Labarium Diagnostic Systems, where she leads enterprise legal strategy, regulatory affairs, and compliance architecture across a rapidly scaling diagnostics platform. She is responsible for ensuring the company operates at the highest standards of regulatory integrity while enabling agile growth in a complex and evolving healthcare environment.",
      "Ms. McLeod brings deep expertise in healthcare law, including HIPAA, fraud and abuse statutes, reimbursement compliance, and laboratory regulatory frameworks. She has successfully built and operationalized compliance programs that support multi-state expansion, payer engagement, and strategic partnerships. Ms. McLeod has served as General Counsel and Chief Compliance Officer at various leading institutions in healthcare including Cincinnati Children's Hospital, SCL Health, Biogen Idec, Novation (now Vizient), Triad Hospitals (HCA Healthcare), and is a former partner at K&L Gates.",
      "At Labarium, she plays a critical role in de-risking growth initiatives, structuring compliant commercialization pathways, and aligning legal strategy with long-term enterprise value creation.",
    ],
    expertise: ["Healthcare Law", "OIG Compliance", "Regulatory Affairs"],
  },
  {
    name: "Doug Masters",
    title: "Vice President, Market Access & National Accounts",
    initials: "DM",
    color: "#0c3567",
    paragraphs: [
      "Doug Masters is Vice President of Market Access & National Accounts at Labarium Diagnostic Systems, where he leads national payer strategy, reimbursement optimization, and enterprise account development. He is responsible for securing coverage, driving favorable reimbursement, and accelerating adoption across health plans, integrated delivery networks, and employer-sponsored populations.",
      "Mr. Masters is a recognized leader in managed care and diagnostics commercialization, with a track record of aligning innovative testing solutions with payer economics and clinical value frameworks. He has previously held senior commercial roles with NeoGenomics Laboratories, Atherotech, Esoteric, and DIANON Systems. He is a graduate of the U.S. Naval Academy and the former Protocol Officer for General Norman Schwartzkopf in the U.S. Central Command during Operation Desert Shield and Desert Storm. He has consistently driven revenue acceleration by expanding access pathways and negotiating high-impact payer agreements.",
      "At Labarium, he is instrumental in positioning the company for durable, reimbursement-driven growth by bridging clinical innovation with scalable market access strategy.",
    ],
    expertise: ["Market Access", "Payer Strategy", "Reimbursement Optimization"],
  },
  {
    name: "Cory Weaver",
    title: "Vice President, M&A and New Ventures",
    initials: "CW",
    color: "#0a3060",
    paragraphs: [
      "Cory Weaver is Vice President of M&A and New Ventures at Labarium Diagnostic Systems, where he leads corporate development, strategic transactions, and new platform creation. He is responsible for identifying and executing inorganic growth opportunities, including acquisitions, partnerships, and adjacent business models that expand Labarium's capabilities and market reach.",
      "Mr. Weaver brings a disciplined, strategy-led approach to growth, with experience across sourcing, diligence, structuring, and post-transaction integration. His work focuses on unlocking synergies, accelerating revenue expansion, and enhancing enterprise value through targeted investments. He has previously founded and exited from various ventures including Havn Healthcare, Lifebridge Psychiatry, and AxisPointe.",
      "At Labarium, he plays a central role in shaping the company's long-term growth trajectory by building a diversified, innovation-driven diagnostics platform.",
    ],
    expertise: ["M&A Strategy", "Corporate Development", "New Ventures"],
  },
  {
    name: "Peter Bryant-Greenwood, MD, MBA, FACHE, FCAP",
    title: "Medical Director",
    initials: "PG",
    color: "#1a4a6e",
    paragraphs: [
      "Dr. Peter Bryant-Greenwood serves as Medical Director at Labarium Diagnostic Systems, where he leads clinical strategy, medical oversight, and translational diagnostics innovation across the company's precision medicine platform. A board-certified pathologist with advanced training in molecular pathology, cytopathology, and clinical proteomics, Dr. Bryant-Greenwood brings more than two decades of experience at the intersection of laboratory medicine, biotechnology, and clinical research.",
      "Dr. Bryant-Greenwood has held senior leadership roles across nationally recognized institutions, including serving as Medical Director of Advanced Diagnostic Laboratories at National Jewish Health, where he oversaw seven laboratories and more than 130 personnel supporting pharmaceutical, biotech, and diagnostics partnerships. He has led large-scale clinical and translational programs, including companion diagnostics development, multi-omic clinical trials, and FDA-regulated assay commercialization, generating significant contract and programmatic value.",
      "Earlier in his career, he served as Director of Pathology and Laboratory Services at The Queen's Medical Center, where he built one of the first clinical molecular genomics laboratories in Hawaii and led a $138 million laboratory enterprise with more than 350 employees. He has been a principal investigator on NIH-funded research, contributed to multiple patented diagnostic technologies, and authored numerous peer-reviewed publications advancing molecular diagnostics and proteomics.",
      "At Labarium, Dr. Bryant-Greenwood plays a central role in clinical validation, regulatory strategy, and the development of next-generation diagnostic solutions, ensuring the company's platform is aligned with evolving standards in precision medicine, reimbursement, and clinical utility.",
    ],
    expertise: ["Molecular Pathology", "Clinical Proteomics", "Translational Diagnostics"],
  },
  // Randy Peters removed from leadership team
  // {
  //   name: "Randy Peters",
  //   title: "Vice President, Commercial & Public Affairs",
  //   initials: "RP",
  //   color: "#0e3f7a",
  //   paragraphs: [
  //     "Randy Peters is Vice President of Commercial & Public Affairs at Labarium Diagnostic Systems...",
  //   ],
  //   expertise: ["Commercial Strategy", "Public Affairs", "Stakeholder Relations"],
  // },
];

const STATS = [
  { value: "200+", label: "Combined Years of Experience" },
  { value: "160+", label: "Team Members" },
  { value: "100+", label: "M&A Transactions" },
  { value: "$5B+", label: "Capital Raised" },
];

export default function LeadershipPage() {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <>
      <PageHero eyebrow="COMPANY" title="Leadership" highlight="Team" subtitle="The experienced executives and visionaries guiding Labarium's mission to become The Healthcare Decision Company." />

      {/* Stats */}
      <div style={{ background: BRAND.white, borderBottom: `1px solid ${BRAND.lightGray}` }}>
        <div className="stat-grid rx-pad" style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "repeat(4,1fr)" }}>
          {STATS.map((s, i) => (
            <div key={i} style={{ padding: "36px 24px", borderRight: i < 3 ? `1px solid ${BRAND.lightGray}` : "none", textAlign: "center" }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 44, color: BRAND.navy, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.15em", color: BRAND.textMid, textTransform: "uppercase", marginTop: 8 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rx-pad" style={{ background: BRAND.offWhite, padding: "72px 32px 96px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 28 }}>
            {LEADERS.map((leader, i) => (
              <div
                key={i}
                onClick={() => setActiveCard(activeCard === i ? null : i)}
                style={{
                  background: BRAND.white,
                  borderRadius: 20,
                  border: `1.5px solid ${activeCard === i ? BRAND.navy : BRAND.lightGray}`,
                  overflow: "hidden",
                  boxShadow: activeCard === i ? "0 20px 56px rgba(0,53,122,0.16)" : "0 4px 20px rgba(0,53,122,0.05)",
                  cursor: "pointer",
                  transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
                }}
                onMouseEnter={e => { if (activeCard !== i) { e.currentTarget.style.transform = "translateY(-6px)"; e.currentTarget.style.boxShadow = "0 24px 64px rgba(0,53,122,0.14)"; } }}
                onMouseLeave={e => { if (activeCard !== i) { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,53,122,0.05)"; } }}
              >
                {/* Photo / color header — full width, tall enough for headshots */}
                <div style={{
                  position: "relative",
                  height: leader.photo ? 240 : 160,
                  background: leader.photo
                    ? "transparent"
                    : `linear-gradient(160deg, ${leader.color} 0%, ${leader.color}dd 60%, #001a45 100%)`,
                  overflow: "hidden",
                }}>
                  {/* Gold top stripe */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight}, ${BRAND.gold})`, zIndex: 2 }} />

                  {leader.photo ? (
                    <img
                      src={leader.photo}
                      alt={leader.name}
                      style={{
                        width: "100%", height: "100%",
                        objectFit: "cover",
                        objectPosition: "center top",
                        display: "block",
                      }}
                    />
                  ) : (
                    <>
                      {/* Subtle dot pattern */}
                      <div style={{ position: "absolute", inset: 0, opacity: 0.07 }}>
                        <svg width="100%" height="100%">
                          <defs><pattern id={`pd${i}`} width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="white"/></pattern></defs>
                          <rect width="100%" height="100%" fill={`url(#pd${i})`}/>
                        </svg>
                      </div>
                      {/* Large initials centered */}
                      <div style={{
                        position: "absolute", inset: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <div style={{
                          width: 80, height: 80, borderRadius: "50%",
                          background: "rgba(201,169,110,0.15)",
                          border: "2px solid rgba(201,169,110,0.35)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontFamily: "'Playfair Display', serif", fontWeight: 700,
                          fontSize: 28, color: BRAND.gold,
                        }}>
                          {leader.initials}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Bottom gradient fade into white card body */}
                  {leader.photo && (
                    <div style={{
                      position: "absolute", bottom: 0, left: 0, right: 0, height: 80,
                      background: "linear-gradient(transparent, rgba(255,255,255,0.95))",
                    }} />
                  )}
                </div>

                {/* Card body */}
                <div style={{ padding: "20px 28px 28px" }}>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 20, color: BRAND.navy, margin: "0 0 5px", lineHeight: 1.25 }}>
                    {leader.name}
                  </h3>
                  <div style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 600,
                    letterSpacing: "0.1em", color: BRAND.gold, textTransform: "uppercase",
                    marginBottom: 14,
                  }}>
                    {leader.title}
                  </div>

                  <div style={{ width: 32, height: 1.5, background: `linear-gradient(90deg, ${BRAND.gold}, rgba(201,169,110,0.2))`, marginBottom: 14 }} />

                  {activeCard === i ? (
                    <div style={{ margin: "0 0 16px" }}>
                      {leader.paragraphs.map((para, pi) => (
                        <p key={pi} style={{
                          fontFamily: "'Inter', sans-serif", fontSize: 13,
                          color: BRAND.textMid, lineHeight: 1.8,
                          margin: pi < leader.paragraphs.length - 1 ? "0 0 14px" : 0,
                        }}>
                          {para}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p style={{
                      fontFamily: "'Inter', sans-serif", fontSize: 13,
                      color: BRAND.textMid, lineHeight: 1.8,
                      margin: "0 0 16px",
                    }}>
                      {leader.paragraphs[0].substring(0, 110) + "…"}
                    </p>
                  )}

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                    {leader.expertise.map(tag => (
                      <span key={tag} style={{
                        background: "rgba(0,53,122,0.07)", color: BRAND.navy,
                        fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700,
                        letterSpacing: "0.06em", padding: "4px 10px", borderRadius: 20,
                        border: "1px solid rgba(0,53,122,0.1)",
                      }}>
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div style={{
                    fontFamily: "'Inter', sans-serif", fontSize: 11, fontWeight: 700,
                    color: BRAND.gold, letterSpacing: "0.12em",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {activeCard === i ? "SHOW LESS ▴" : "READ BIO ▾"}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Vision CTA */}
          <div style={{ marginTop: 64, background: BRAND.navy, borderRadius: 20, padding: "56px 48px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: BRAND.gold }} />
            <div style={{ fontFamily: "'Barlow', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: BRAND.gold, marginBottom: 12 }}>OUR VISION</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 36, color: BRAND.white, margin: "0 0 20px", lineHeight: 1.2 }}>
              Building The Healthcare Decision Company
            </h2>
            <p style={{ fontFamily: "'Barlow', sans-serif", fontSize: 15, color: "rgba(255,255,255,0.65)", lineHeight: 1.8, maxWidth: 640, margin: "0 auto 32px" }}>
              Our leadership team is unified around a single mission: transform independent laboratory data into contextual, actionable insights that drive better clinical decisions — for every patient, every time.
            </p>
            <p style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontSize: 18, color: BRAND.gold, margin: 0 }}>
              "Better information. Better decisions. Better lives."
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
